import PageBox from "PFComponents/box/page_box";
import { Button } from "PFComponents/button";
import ButtonLink from "PFComponents/button/button_link";
import Card from "PFComponents/card/card";
import CardHeader from "PFComponents/card/card_header";
import DataTable from "PFComponents/data_table/data_table";
import Modal from "PFComponents/modal/modal";
import SimplePagination from "PFComponents/pagination/simple_pagination";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import CloseIcon from "PFIcons/close.svg";
import SaasAdminHeader from "PFModules/saas_admin/shared/saas_admin_header";
import PropTypes from "prop-types";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import css from "./imports_list_page.less";

const ImportDetailsModal = ({ dataId, imports, handleClose }) => {
  const data = useMemo(() => {
    if (dataId === null || dataId === undefined) {
      return null;
    }

    return imports.find((im) => im.id === dataId);
  }, [dataId]);

  if (data === null || data === undefined) {
    return null;
  }

  return (
    <Modal style={{ maxWidth: 1000, margin: "0 auto" }} closeOnBackdropClick handleClose={handleClose}>
      <Card
        header={
          <CardHeader
            title={`Import ${dataId} details`}
            style={{ padding: "20px 20px" }}
            action={<CloseIcon onClick={handleClose} height={18} width={18} />}
          />
        }
      >
        <pre className={css.importDetailsCode}>
          <code>{JSON.stringify(JSON.parse(data.result), undefined, 2)}</code>
        </pre>
      </Card>
    </Modal>
  );
};

ImportDetailsModal.propTypes = {
  dataId: PropTypes.oneOf([PropTypes.undefined, PropTypes.number]),
  imports: PropTypes.array,
  handleClose: PropTypes.func
};

const ImportsListPage = ({ accountName, imports, fetchImports, meta }) => {
  const { t } = useTranslation("saasAdmin");

  const { formatDate } = useDateFormatter();
  const [detailsModalId, setDetailsModalId] = useState(null);

  const cellRenderer = (attribute) => (attribute ? <span>{attribute}</span> : null);
  const actionsCell = (id, details) => (
    <>
      <Button kind="tertiary" small onClick={() => setDetailsModalId(id)}>
        Details
      </Button>
      <span className={css.badgeArea}>
        {details.errors.length > 0 && <div className={css.errorBadge}>!</div>}
        {details.warnings.length > 0 && <div className={css.warningBadge}>!</div>}
      </span>
    </>
  );

  return (
    <div className={css.importsListPage}>
      <ImportDetailsModal
        dataId={detailsModalId}
        handleClose={() => setDetailsModalId(null)}
        imports={imports}
      />
      <div className={css.importListPageHeader}>
        <SaasAdminHeader title="Imports" subTitle={`(for ${accountName})`} fixedWidth={false} />
        <div className={css.importListPageHeaderActions}>
          <Button kind="secondary" onClick={() => fetchImports()}>
            {t("imports.refresh")}
          </Button>
          <ButtonLink kind="primary" href="/admin/users/import">
            {t("imports.newImportButton")}
          </ButtonLink>
        </div>
      </div>
      <PageBox
        style={{
          marginBottom: "2rem",
          paddingTop: "0.8rem"
        }}
      >
        <DataTable
          columns={[
            {
              headerName: "ID",
              renderCell: (data) => cellRenderer(data.id)
            },
            {
              headerName: "Source",
              renderCell: (data) => cellRenderer(data.source)
            },
            {
              headerName: "Imported By",
              renderCell: (data) => data.creator && cellRenderer(data.creator.email)
            },
            {
              headerName: "Records in CSV",
              renderCell: (data) => cellRenderer(JSON.parse(data.result).enqueued)
            },
            {
              headerName: "Records Processed",
              renderCell: (data) => cellRenderer(data.records_processed)
            },
            {
              headerName: "Users created",
              renderCell: (data) => cellRenderer(data.records_created)
            },
            {
              headerName: "Created at",
              renderCell: (data) => cellRenderer(formatDate(data.created_at))
            },
            {
              headerName: "Details",
              renderCell: (data) => actionsCell(data.id, JSON.parse(data.result))
            }
          ]}
          data={imports}
        />
      </PageBox>
      <SimplePagination currentPage={meta?.page} totalPages={meta?.totalPages} handleClick={fetchImports} />
    </div>
  );
};

ImportsListPage.propTypes = {
  imports: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      source: PropTypes.string,
      result: PropTypes.string,
      records_processed: PropTypes.number,
      records_created: PropTypes.number,
      created_at: PropTypes.strting,
      creator: PropTypes.oneOf([
        PropTypes.undefined,
        PropTypes.shape({
          email: PropTypes.string
        })
      ])
    })
  ),
  meta: PropTypes.shape({
    page: PropTypes.number,
    perPage: PropTypes.number,
    total: PropTypes.number,
    totalPages: PropTypes.number
  }),
  accountName: PropTypes.string,
  fetchImports: PropTypes.func
};

export default ImportsListPage;
